import React, { memo, useCallback, useContext } from 'react'
import OfferPrice from 'components/Common/OfferPricing/Price/OfferPrice'
import {
  PriceContainer,
} from 'components/Common/OfferPricing/styles'
import TextButton from 'LuxKit/Button/TextButton'
import { isCruiseV1Offer } from 'lib/offer/offerTypes'
import * as Analytics from 'analytics/analytics'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import styled from 'styled-components'
import FlashTourSoldOutOrUnavailable from './FlashTourSoldOutOrUnavailable'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import { generateTourCheckoutItem } from 'lib/checkout/accommodation/cart'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import Group from 'components/utils/Group'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useIntl } from 'react-intl'

const StyledPriceContainer = styled(PriceContainer)`
  display: flex;
  flex-direction: column;
  padding-bottom: ${rem(6)};

`

interface Props {
  memberId?: string
  availableForPurchase: boolean
  canInstantBook?: boolean
  durationLabel: string
  originAirport?: App.Airport
  offerFlightsEnabled?: boolean
  offer: App.TourOffer
  packageOption: App.TourPackage
  flightPrice?: number
  shouldDisplayValue?: boolean
  showFlights?: boolean
  className?: string
}

function FlashTourPackagePricing({
  memberId,
  durationLabel,
  packageOption,
  offer,
  availableForPurchase,
  originAirport,
  shouldDisplayValue,
  showFlights,
  offerFlightsEnabled,
  className,
  flightPrice = 0,
}: Props) {
  const intl = useIntl()
  const { typeLabel, flightDestinationPort, saleUnit } = offer
  const { code: originAirportCode } = originAirport || {}

  const hasPrice = !!packageOption.price
  const flightCacheOff = showFlights && originAirport && (offer.flightCacheDisabled || !offer.flightPrices[originAirport?.code])
  const originIsDestination = originAirport && flightDestinationPort === originAirportCode

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isCruiseOffer = isCruiseV1Offer(offer)

  const fireSelectPackageEvent = useCallback((keepBrowsing = false) => {
    if (isCruiseOffer) {
      Analytics.trackClientEvent({
        subject: 'select_package',
        action: 'clicked',
        category: 'cruises_v1',
        type: 'interaction',
      })
    }

    const items = generateTourCheckoutItem(
      offer,
      packageOption,
      { adults: isCruiseOffer ? 2 : 1 },
    )

    dispatch(addToCheckoutCart(items))

    if (keepBrowsing) {
      showSnackbar('Your item has been added to the cart.', 'success')
      return
    }

    dispatch(navigateToCheckout())
  }, [dispatch, isCruiseOffer, offer, packageOption])

  const showRegisterModal = useCallback(async() => {
    const hotelOfferDetails: App.AccountModalOfferDetails = {
      title: offer.name,
      duration: packageOption.duration,
      value: packageOption.price,
      imageId: offer.image.id,
      imageTitle: offer.name,
    }

    const result = await showModal<AccountAccessModalResult>(<AccountAccessModal
      initialMode="login"
      hotelOfferDetails={hotelOfferDetails}
    />)

    if (result.loggedIn) {
      fireSelectPackageEvent()
    }
  }, [offer, packageOption, showModal, fireSelectPackageEvent])

  return <Group direction="horizontal" desktopHorizontalAlign="stretch" className={className}>
    <StyledPriceContainer>
      <OfferPrice
        offer={offer}
        durationLabel={durationLabel}
        offerSaleUnit={saleUnit}
        label={typeLabel}
        shouldShowValue={shouldDisplayValue}
        showFlights={showFlights}
        price={packageOption.price}
        value={packageOption.value}
        showDiscountPill
      />
      <VerticalSpacer gap={4}>
        <PriceRowAgentHubCommission
          productType={offer.productType}
          offerId={offer.id}
          offerLocations={offer.locations}
          offerTotal={packageOption.price}
          packageId={packageOption.id}
        />
        <FlashTourSoldOutOrUnavailable
          availableForPurchase={availableForPurchase}
          hasPrice={hasPrice}
        >
          <Group direction="vertical" desktopDirection="horizontal" desktopHorizontalAlign="stretch" gap={8}>
            <TextButton
              kind="primary"
              fit="flex"
              data-testid="TourBookingBtn"
              onClick={!memberId ? () => showRegisterModal() : () => fireSelectPackageEvent(false)}
            >
              {intl.formatMessage({
                id: 'noEHCxGrfw',
                defaultMessage: 'Check Availability',
                description: 'Button to check tour package availability',
              })}
            </TextButton>
          </Group>
        </FlashTourSoldOutOrUnavailable>
      </VerticalSpacer>
    </StyledPriceContainer>
    {originAirport && showFlights && offerFlightsEnabled &&
      <StyledPriceContainer>
        <OfferPrice
          offer={offer}
          hasClause
          isFlightPrice
          flightCacheOff={flightCacheOff}
          durationLabel={durationLabel}
          offerSaleUnit="package"
          label={`${typeLabel} + Flights`}
          shouldShowValue={shouldDisplayValue && !flightCacheOff}
          showFlights={showFlights}
          price={flightPrice && packageOption.price + flightPrice}
          value={flightPrice && packageOption.value + flightPrice}
          data-testid="pricing-with-flights"
          originAirport={originAirport}
          noFlightsFromOrigin={originIsDestination}
        />
        <FlashTourSoldOutOrUnavailable
          availableForPurchase={availableForPurchase}
          hasPrice={hasPrice}
        />
      </StyledPriceContainer>}
  </Group>
}

export default memo(FlashTourPackagePricing)
